import { useState, useEffect, useRef } from "react";
import { db, storage, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

import Header from "../components/Header";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Kiosk.module.css";
import ErrMsg from "../ErrMsg";
import { useDesignVersion } from "../DesignVersionContext";
import KioskV2 from "./KioskV2";
import { BlockInput, ToggleSwitch, AddressInput, PhotoInput, photoResize } from "../components/Inputs";
import { BlockButton, ConfirmButton, ListButton, TextButton } from "../components/Buttons";
import { KioskType, useKiosk } from "../KioskContext";

const Kiosk = () => {
    const { version } = useDesignVersion();
    const [editKiosk, setEditKiosk] = useState<boolean>(false);
    const [target, setTarget] = useState<KioskType | null>(null);
    const [required, setRequired] = useState<boolean>(false);

    useEffect(() => {
        document.body.style.overflow = editKiosk ? "hidden" : "auto";
    }, [editKiosk]);

    if (version === "v2") {
        return <KioskV2 />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>키오스크 관리</h4>
                    <p className={common["text--copy"]}>
                        키오스크 정보를 변경하거나 삭제할 수 있습니다.
                        <br />
                        등록을 위해선 키오스크에 앱을 설치해주세요.
                    </p>

                    <PendingKiosk
                        setRequired={() => setRequired(true)}
                        setKiosk={(kiosk: KioskType) => setTarget(kiosk)}
                        edit={() => setEditKiosk(true)}
                    />
                    <KioskTable
                        setRequired={() => setRequired(false)}
                        setKiosk={(kiosk: KioskType) => setTarget(kiosk)}
                        edit={() => setEditKiosk(true)}
                    />
                </div>
            </div>

            {editKiosk && (
                <EditKiosk required={required} kiosk={target as KioskType} close={() => setEditKiosk(false)} />
            )}
        </>
    );
};

const PendingKiosk = ({
    setRequired,
    setKiosk,
    edit,
}: {
    setRequired: () => void;
    setKiosk: (kiosk: KioskType) => void;
    edit: () => void;
}) => {
    const { pendings } = useKiosk();

    const editInfo = (target: KioskType) => {
        setRequired();
        edit();
        setKiosk(target);
    };

    return (
        <>
            {pendings.length > 0 && (
                <>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>
                            새로 생성된 키오스크
                        </p>
                    </div>
                    <div className={style["wrapper--kiosk-pending"]}>
                        {pendings.map((kiosk, index) => (
                            <div
                                key={index}
                                className={[style["unit--kiosk-list"], style["unit--kiosk-list--pending"]].join(" ")}
                                onClick={() => editInfo(kiosk)}
                            >
                                <div
                                    className={[style["container--rows"], style["container--rows--pending"]].join(" ")}
                                >
                                    <p className={style["text--eyebrow"]}>{kiosk.id}</p>
                                    <TextButton text="정보 입력하기" onClick={() => editInfo(kiosk)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

const KioskTable = ({
    setRequired,
    setKiosk,
    edit,
}: {
    setRequired: () => void;
    setKiosk: (kiosk: KioskType) => void;
    edit: () => void;
}) => {
    const { kiosks } = useKiosk();

    const [search, setSearch] = useState<string>("");

    const editInfo = (kiosk: KioskType) => {
        setRequired();
        edit();
        setKiosk(kiosk);
    };

    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});
    const [disableConfirmMap, setDisableConfirmMap] = useState<Record<string, boolean>>({});

    const toggleKiosk = async (kiosk: KioskType) => {
        setDisableConfirmMap((prev) => ({ ...prev, [kiosk.docId]: true }));

        return updateDoc(doc(db, "kiosk", kiosk.docId), {
            status: kiosk.status === "INACTIVE" ? "ACTIVE" : "INACTIVE",
        })
            .then(() => {
                setDisableConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
            })
            .catch((err) => {
                setDisableConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                console.error(err);
            });
    };

    const emptyKiosk = async (kiosk: KioskType) => {
        setLoadingConfirmMap((prev) => ({ ...prev, [kiosk.docId]: true }));

        return httpsCallable(
            functions,
            "emptyKiosk"
        )({
            docId: kiosk.docId,
        })
            .then((result) => {
                setLoadingConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                alert("수거가 완료되었습니다.");
            })
            .catch((err) => {
                setLoadingConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                console.error(err);
                alert(ErrMsg[err.message] || "에러가 발생했습니다");
            });
    };

    return (
        <>
            {kiosks && (
                <>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>키오스크 ID</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>
                            주소 및 위치
                        </p>
                        <p
                            className={[
                                style["text--eyebrow"],
                                style["text--eyebrow--title"],
                                style["text--eyebrow--number"],
                            ].join(" ")}
                        >
                            현재 투입량
                        </p>
                    </div>
                    <div className={style["wrapper--kiosk-list"]}>
                        <input
                            id="search-kiosk"
                            className={common["input--kiosk-filter"]}
                            type="text"
                            placeholder="아이디, 이름 또는 주소로 검색"
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                        {kiosks
                            .filter((kiosk) => {
                                return (
                                    search === "" ||
                                    (search &&
                                        (kiosk.id.toLowerCase().includes(search.toLowerCase()) ||
                                            kiosk.name!.toLowerCase().includes(search.toLowerCase()) ||
                                            kiosk.address!.toLowerCase().includes(search.toLowerCase())))
                                );
                            })
                            .map((kiosk, index) => (
                                <div
                                    key={index}
                                    className={style["unit--kiosk-list"]}
                                    onClick={(e) => {
                                        if (
                                            (e.target instanceof HTMLInputElement && e.target.type === "checkbox") ||
                                            e.target instanceof HTMLSpanElement ||
                                            e.target instanceof HTMLButtonElement
                                        )
                                            return;
                                        editInfo(kiosk);
                                    }}
                                >
                                    <div className={style["container--rows"]}>
                                        <p className={style["text--eyebrow"]}>
                                            {kiosk.id}
                                            <span>{kiosk.docId}</span>
                                        </p>
                                        <p className={style["text--eyebrow"]}>
                                            {kiosk.name}
                                            {kiosk.name!.length > 8 && <span>{kiosk.name}</span>}
                                        </p>
                                        <p className={style["text--eyebrow"]}>
                                            {kiosk.address}
                                            {kiosk.address!.length > 20 && <span>{kiosk.address}</span>}
                                        </p>
                                        <p
                                            className={[style["text--eyebrow"], style["text--eyebrow--number"]].join(
                                                " "
                                            )}
                                        >
                                            {kiosk.currentCapacity}
                                        </p>
                                        <ConfirmButton
                                            loading={loadingConfirmMap[kiosk.docId] ?? false}
                                            onClick={() => emptyKiosk(kiosk)}
                                            text="수거"
                                        />
                                        {kiosk.status !== "FULL" ? (
                                            <ToggleSwitch
                                                checked={kiosk.status === "ACTIVE"}
                                                disabled={disableConfirmMap[kiosk.docId] ?? false}
                                                onChange={() => toggleKiosk(kiosk)}
                                            />
                                        ) : (
                                            <p className={style["text--eyebrow--caution"]}>꽉참</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </>
            )}
        </>
    );
};

const EditKiosk = ({ kiosk, required = false, close }: { kiosk: KioskType; required: boolean; close: () => void }) => {
    const init = useRef(true);

    const [changed, setChanged] = useState<{ [key: string]: boolean }>({
        name: false,
        address: false,
        message: false,
        maxCapacity: false,
        photo: false,
    });

    const [name, setName] = useState<string>(kiosk.name ? kiosk.name : "");
    const [nameError, setNameError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (name && name.length < 2) {
            setNameError(ErrMsg["kiosk/name-invalid"]);
        } else {
            setNameError("");
            setChanged((prev) => ({ ...prev, name: true }));
        }
    }, [name]);

    const [address, setAddress] = useState<string>(kiosk.address ? kiosk.address : "");
    const [addressError, setAddressError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (address && address.length < 2) {
            setAddressError(ErrMsg["kiosk/address-invalid"]);
        } else {
            setAddressError("");
            setChanged((prev) => ({ ...prev, address: true }));
        }
    }, [address]);

    const [message, setMessage] = useState<string>(kiosk.message ? kiosk.message : "");
    useEffect(() => {
        if (init.current) return;
        setChanged((prev) => ({ ...prev, message: true }));
    }, [message]);

    const [maxCapacity, setMaxCapacity] = useState<string>(kiosk.maxCapacity ? String(kiosk.maxCapacity) : "");
    const [maxCapacityError, setMaxCapacityError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (maxCapacity && Number(maxCapacity) <= 0) {
            setMaxCapacityError(ErrMsg["kiosk/maxCapacity-invalid"]);
        } else {
            setMaxCapacityError("");
            setChanged((prev) => ({ ...prev, maxCapacity: true }));
        }
    }, [maxCapacity]);

    const [photo, setPhoto] = useState<File | null>(null);
    const [photoUrl] = useState<string>(kiosk.photo ?? ""); // 기존 이미지 URL 저장
    const [photoErr, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if (init.current) return;

        if (!Boolean(photo)) {
            // 신규 등록(required=true)이거나 기존 사진이 없는 경우에만 에러 표시
            if (required || !kiosk.photo) {
                setPhotoError(ErrMsg["kiosk/photo-invalid"]);
            } else {
                setPhotoError(""); // 기존 키오스크 편집 시 기존 사진이 있으면 에러 없음
            }
        } else {
            setPhotoError("");
            setChanged((prev) => ({ ...prev, photo: true }));
        }
    }, [photo, kiosk.docId, kiosk.photo, required]);

    useEffect(() => {
        if (init.current) {
            init.current = false;
        }
    }, [init]);

    const [loading, setLoading] = useState<boolean>(false);

    const edit = async () => {
        if (!name) {
            setNameError(ErrMsg["kiosk/name-invalid"]);
            return;
        } else if (!address) {
            setAddressError(ErrMsg["kiosk/address-invalid"]);
            return;
        } else if (!maxCapacity) {
            setMaxCapacityError(ErrMsg["kiosk/maxCapacity-invalid"]);
            return;
        } else if (required && !photo) {
            // 신규 등록 시에만 photo 필수 체크
            setPhotoError(ErrMsg["kiosk/photo-invalid"]);
            return;
        }

        setLoading(true);

        const data: { [key: string]: string | number } = {};

        if (required) {
            // 최초 등록 시
            data["status"] = "ACTIVE";
            data["name"] = name;
            data["address"] = address;
            data["message"] = message;
            data["maxCapacity"] = maxCapacity;
            data["currentCapacity"] = 0;
            data["timestamp"] = new Date().getTime();
        } else {
            if (changed["name"]) data["name"] = name;
            if (changed["address"]) data["address"] = address;
            if (changed["message"]) data["message"] = message;
            if (changed["maxCapacity"]) data["maxCapacity"] = maxCapacity;
        }

        // 새 이미지가 업로드된 경우에만 처리
        if (photo && changed["photo"]) {
            try {
                let resizedPhoto = await photoResize(photo, "0");

                const storageRef = ref(storage, `kiosk/${kiosk.docId}/image/0`);
                const uploadTask = uploadBytesResumable(storageRef, resizedPhoto);

                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            // 업로드 진행률 계산
                            const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setProgress(prog);
                        },
                        (error) => {
                            console.error("Upload failed", error);
                            reject(new Error("Upload failed"));
                        },
                        () => {
                            // 업로드 완료 후 다운로드 URL 얻기
                            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                                data["photo"] = downloadURL;
                                resolve(true);
                            });
                        }
                    );
                });
            } catch (err) {
                console.error(err);
                return 0;
            }
        }

        if (Object.keys(data).length === 0) {
            hide();
            return;
        }

        return updateDoc(doc(db, "kiosk", kiosk.docId), data)
            .then(() => {
                setLoading(false);
                hide();
            })
            .catch((err) => {
                setLoading(false);
                console.error(err);
            });
    };

    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deleteKiosk = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "kioskDelete"
        )({
            docId: kiosk.docId,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>키오스크 관리</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay"]}>
                        <div className={style["wrapper--overlay--inputs"]}>
                            <h4 className={common["text--overlay"]}>키오스크 기본 정보</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput
                                    type="text"
                                    placeholder="제목"
                                    value={name}
                                    setValue={setName}
                                    error={nameError}
                                />

                                <AddressInput
                                    placeholder="주소"
                                    value={address}
                                    setValue={setAddress}
                                    error={addressError}
                                />

                                <BlockInput
                                    type="text"
                                    placeholder="사용 불가 시 표시 메시지"
                                    value={message}
                                    setValue={setMessage}
                                />
                            </div>

                            <h4 className={common["text--overlay"]}>키오스크 투입 정보</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput
                                    type="number"
                                    placeholder="최대 투입 개수"
                                    value={maxCapacity}
                                    setValue={setMaxCapacity}
                                    error={maxCapacityError}
                                />
                                <BlockButton
                                    type={deleteBtn}
                                    text={deleteBtn === "Delete" ? "삭제하기" : "삭제 확인하기"}
                                    loading={loadingDelete}
                                    onClick={deleteKiosk}
                                />
                            </div>
                        </div>
                        <div className={style["wrapper--overlay--photo"]}>
                            <h4 className={common["text--overlay"]}>키오스크 기본 사진</h4>
                            <div className={style["unit--overlay--inputs"]}>
                                <PhotoInput
                                    photo={photo}
                                    setPhoto={(file) => setPhoto(file)}
                                    progress={progress}
                                    error={photoErr}
                                    existingImageUrl={photoUrl} // 기존 이미지 URL 전달
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />

                    <ListButton text="수정하기" loading={loading} onClick={edit} />
                </div>
            </div>
        </div>
    );
};

export default Kiosk;
